/**
 * Free→Paid-Nudge-Promotion-Registry (M-N1, architecture-nudge.md §7.2).
 *
 * SINGLE SOURCE OF TRUTH für alle Nudge-Promotion-IDs, den Upgrade-Href-Bau
 * und das Rückwärts-Parsing der `?ref=`-Query auf `/dashboard/abonnement`.
 * Bündelt IDs + Href-Bau + Parsing an einer Stelle, damit über die 6
 * Nudge-Flächen (UsageMeter, 3× Hard-Limit-Banner, FeatureTeaser,
 * ReceivedInvoices-Lock) keine Tippfehler-Drift entstehen kann.
 *
 * Bewusst KEINE weitere Abstraktion (kein Hook, kein Context, kein Provider) —
 * die Funktionen sind pur und werden direkt importiert (architecture-nudge.md §7.2).
 *
 * @module config/nudgePromotions
 */

import type { TierId } from './tiers';

/** Ziel-Route aller Nudge-Upgrade-CTAs (`routes/dashboardChildren.tsx`). */
const UPGRADE_ROUTE = '/dashboard/abonnement';

/** Name des Query-Parameters, unter dem die auslösende Promotion-ID an die Zielseite übergeben wird. */
export const NUDGE_REF_PARAM = 'ref';

/**
 * Name des Query-Parameters, der den BEWORBENEN Ziel-Tarif an
 * `/dashboard/abonnement` übergibt (D-1, 2026-07-29). Die Abo-Seite hebt die
 * genannte Spalte hervor und scrollt sie in den Blick — vorher landete jeder
 * Upgrade-Klick unspezifisch oben auf der 3-Spalten-Tabelle, obwohl der Teaser
 * konkret „Jetzt auf Ultra upgraden" versprochen hatte.
 */
export const NUDGE_TIER_PARAM = 'tier';

/**
 * Bekannte Nudge-Promotion-Flächen (GA4 `promotion_id`, architecture-nudge.md §7.2/§7.4):
 * - `usage_meter_soft` — InvoiceUsageMeter, count >= SOFT_THRESHOLD, < limit (M-N2)
 * - `usage_meter_limit` — InvoiceUsageMeter, count >= limit, Dashboard-Besuch VOR einem 403 (M-N2)
 * - `usage_limit_banner` — 403-Moment-Banner in den 3 Dashboards (M-N3)
 * - `feature_teaser_receipt_scan` — FeatureTeaser Belegscanner, `creative_slot` unterscheidet die Fläche (M-N5)
 * - `feature_lock_received_invoices` — Lock-Card auf ReceivedInvoicesPage (M-N5)
 * - `feature_teaser_bank_reconcile` — FeatureTeaser Bank-Abgleich, Ziel-Tier Ultra (Pricing-Repackaging
 *   PR-T6, D7 generalisierte locked-/Teaser-Regel §3.2)
 * - `feature_teaser_datev_export` — FeatureTeaser DATEV-Export, Ziel-Tier Ultra (PR-T6, D7)
 * - `feature_teaser_open_items` — FeatureTeaser Offene Posten, Ziel-Tier Premium (PR-T6, D7, Take-away PR-T5)
 * - `feature_teaser_vorsteuer_cockpit` — FeatureTeaser Vorsteuer-Cockpit-Summenleiste, Ziel-Tier **Ultra**
 *   (PR-T6, D7, Take-away PR-T5; Ziel-Tier von Premium auf Ultra korrigiert 2026-07-29 — das Gate
 *   `canUseVorsteuerCockpit` verlangt seit dem Marketing-Relaunch `tier === 'ultra'`, der Teaser
 *   bewarb aber weiterhin Premium)
 * - `feature_teaser_travel_expenses` — FeatureTeaser Reisekosten/Dienstreisen, Ziel-Tier Ultra
 *   (neu 2026-07-29: bis dahin sahen Nicht-Ultra-Nutzer gar keine Upgrade-Aufforderung)
 * - `feature_teaser_ustva` — FeatureTeaser USt-Voranmeldungs-Vorschau, Ziel-Tier Ultra
 *   (neu 2026-07-29, analog `feature_teaser_travel_expenses`)
 * - `grace_countdown_open_items` — GraceCountdownBanner OP-Dashboard, befristetes Grandfathering-Fenster
 *   (M-G4, architecture-grace-window.md §5.5; deckt dunning mit ab, §6.3 — keine eigene Dunning-ID)
 * - `grace_countdown_vorsteuer_cockpit` — GraceCountdownBanner Ausgaben-Seite, befristetes
 *   Grandfathering-Fenster (M-G4, architecture-grace-window.md §5.5)
 */
export type NudgePromotionId =
  | 'usage_meter_soft'
  | 'usage_meter_limit'
  | 'usage_limit_banner'
  | 'feature_teaser_receipt_scan'
  | 'feature_lock_received_invoices'
  | 'feature_teaser_bank_reconcile'
  | 'feature_teaser_datev_export'
  | 'feature_teaser_open_items'
  | 'feature_teaser_vorsteuer_cockpit'
  | 'feature_teaser_travel_expenses'
  | 'feature_teaser_ustva'
  | 'grace_countdown_open_items'
  | 'grace_countdown_vorsteuer_cockpit';

/**
 * Laufzeit-Pendant zu {@link NudgePromotionId}. `NudgePromotionId` ist ein
 * compile-only Union-Typ ohne Laufzeit-Repräsentation — `parseNudgeRef`
 * braucht eine echte Whitelist zur Validierung eines Query-Strings, kein
 * bloßer `as`-Cast (sonst wäre jeder String silently "gültig").
 */
const NUDGE_PROMOTION_IDS: readonly NudgePromotionId[] = [
  'usage_meter_soft',
  'usage_meter_limit',
  'usage_limit_banner',
  'feature_teaser_receipt_scan',
  'feature_lock_received_invoices',
  'feature_teaser_bank_reconcile',
  'feature_teaser_datev_export',
  'feature_teaser_open_items',
  'feature_teaser_vorsteuer_cockpit',
  'feature_teaser_travel_expenses',
  'feature_teaser_ustva',
  'grace_countdown_open_items',
  'grace_countdown_vorsteuer_cockpit'
];

/**
 * DER EINE Ort, an dem der beworbene Ziel-Tarif je Nudge-Fläche steht (D-1,
 * 2026-07-29). Vorher wiederholte jede Call-Site den Tarif als roher String im
 * `ctaTo`-Prop (`/dashboard/abonnement?tier=ultra`) — der aber nirgends
 * ausgewertet wurde, also tote Deko war und pro Fläche auseinanderdriften
 * konnte. Jetzt leiten sowohl {@link buildUpgradeHref} (Ziel-Spalte auf der
 * Abo-Seite) als auch der `GraceCountdownBanner` (Tarif-Name im Wortlaut) den
 * Tarif AUS DIESER Tabelle ab.
 *
 * `Record<NudgePromotionId, TierId>` statt `Partial<>`: der Compiler erzwingt
 * damit bei jeder neuen Promotion-ID eine bewusste Tarif-Entscheidung — eine
 * vergessene Zeile ist ein Build-Fehler, kein stiller `undefined`-Fallback auf
 * den falschen Tarif.
 *
 * Zuordnung folgt strikt dem jeweiligen `canUseX`-Gate:
 * Rechnungs-Limit/Offene Posten/Eingangsrechnungen ⇒ Premium; Belegscanner/
 * Bank/DATEV/Vorsteuer-Cockpit/Reisekosten/UStVA ⇒ Ultra.
 */
export const NUDGE_PROMOTION_TARGET_TIER: Record<NudgePromotionId, TierId> = {
  // Free→Premium: die Rechnungs-Mengenbegrenzung fällt mit Premium.
  usage_meter_soft: 'premium',
  usage_meter_limit: 'premium',
  usage_limit_banner: 'premium',
  // `canUseOpenItems` / `ReceivedInvoicesPage`-Gate (`isPremium`) ⇒ Premium.
  feature_teaser_open_items: 'premium',
  feature_lock_received_invoices: 'premium',
  grace_countdown_open_items: 'premium',
  // Ultra-Gates (`tier === 'ultra'` im jeweiligen `config/*Access.ts`).
  feature_teaser_receipt_scan: 'ultra',
  feature_teaser_bank_reconcile: 'ultra',
  feature_teaser_datev_export: 'ultra',
  feature_teaser_vorsteuer_cockpit: 'ultra',
  feature_teaser_travel_expenses: 'ultra',
  feature_teaser_ustva: 'ultra',
  grace_countdown_vorsteuer_cockpit: 'ultra',
};

/**
 * Beworbener Ziel-Tarif einer Nudge-Fläche (Lesezugriff auf
 * {@link NUDGE_PROMOTION_TARGET_TIER}). Reine Funktion, wirft nie.
 */
export function getPromotionTargetTier(id: NudgePromotionId): TierId {
  return NUDGE_PROMOTION_TARGET_TIER[id];
}

/**
 * Baut den Upgrade-Href für eine Nudge-Fläche:
 * `/dashboard/abonnement?ref=<id>&tier=<zielTarif>`.
 *
 * Einzige Quelle für Nudge-CTA-Hrefs — jede Fläche ruft diese Funktion, statt
 * Ziel-Route, Query-Namen oder Ziel-Tarif selbst zu bauen. `ref` trägt die
 * M-N4-Checkout-Attribution, `tier` steuert die Spalten-Hervorhebung auf
 * `/dashboard/abonnement` (D-1). Beide Werte sind aus einer geschlossenen
 * Whitelist abgeleitet, nie Freitext.
 */
export function buildUpgradeHref(id: NudgePromotionId): string {
  const tier = getPromotionTargetTier(id);
  return `${UPGRADE_ROUTE}?${NUDGE_REF_PARAM}=${encodeURIComponent(id)}&${NUDGE_TIER_PARAM}=${encodeURIComponent(tier)}`;
}

/**
 * Parst einen `?ref=`-Query-Wert fail-closed zu einer bekannten
 * {@link NudgePromotionId}. Unbekannter, leerer oder fehlender Wert ⇒
 * `undefined` — es wird NIE Freitext durchgereicht (Injection-/
 * Kardinalitäts-Schutz für GA4-Custom-Parameter). Wirft nie.
 */
export function parseNudgeRef(raw: string | null): NudgePromotionId | undefined {
  if (!raw) {
    return undefined;
  }
  return NUDGE_PROMOTION_IDS.includes(raw as NudgePromotionId) ? (raw as NudgePromotionId) : undefined;
}

/**
 * Laufzeit-Whitelist der gültigen `?tier=`-Werte (D-1). `TierId` ist ein
 * compile-only Union-Typ — {@link parseNudgeTier} braucht eine echte Liste, aus
 * demselben Grund, aus dem {@link NUDGE_PROMOTION_IDS} für `?ref=` existiert.
 * Bewusst hier statt in `tiers.ts`: `TIER_IDS` dort beschreibt die Reihenfolge
 * der Tabellen-SPALTEN, dies hier die zulässigen Query-Werte — zwei Zwecke, die
 * unabhängig voneinander wachsen dürfen.
 */
const NUDGE_TIER_VALUES: readonly TierId[] = ['free', 'premium', 'ultra'];

/**
 * Parst einen `?tier=`-Query-Wert fail-closed zu einer bekannten {@link TierId}.
 * Unbekannter, leerer oder fehlender Wert ⇒ `undefined` (dann hebt die Abo-Seite
 * einfach keine Spalte hervor — kein Fehler, nur kein Fokus). Wirft nie.
 */
export function parseNudgeTier(raw: string | null): TierId | undefined {
  if (!raw) {
    return undefined;
  }
  return NUDGE_TIER_VALUES.includes(raw as TierId) ? (raw as TierId) : undefined;
}
